import { useCallback, useMemo } from 'react';

import { useRouter } from 'expo-router';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { useMinimalCartUi } from 'features/cart/hooks/use-minimal-cart-ui';
import { useProductWizardContext } from 'features/menu/components/product-wizard';
import {
  FlowStep,
  IProductWizardFlow,
  IProductWizardParams,
  ProductWizardStep,
} from 'features/menu/components/product-wizard/types';
import {
  getWizardRouteRedirectTarget,
  resolveCurrentFlowStep,
} from 'features/menu/components/product-wizard/utils';

import { useRoute } from 'hooks/navigation/use-route';
import { useAuthContext } from 'state/auth';
import { selectors, useAppSelector } from 'state/global-state';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { routes } from 'utils/routing';
import { stringToBool } from 'utils/string-To-Bool';
/**
 * Controls product wizard steps navigation,
 * hiding implementation details about router and queryString manipulation.
 */

export const useProductWizardFlow = (): IProductWizardFlow => {
  const router = useRouter();
  const { params } = useRoute<IProductWizardParams>();
  const appliedRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const legacyCartEntries = useAppSelector(selectors.ordering.selectCartEntries);
  const { indexedOffers, indexedMenu } = useUnifiedMenuContext();
  const { isAuthenticated } = useAuthContext();
  const isMinimalCartUIEnabled = useMinimalCartUi();
  const { cartEntries: minimalCartEntries } = useMinimalCart();

  const cartEntries = isMinimalCartUIEnabled ? minimalCartEntries : legacyCartEntries;
  const hasCartEntries = cartEntries.length > 0;

  const isProductReviewFromCart = stringToBool(params.isProductReviewFromCart);

  const defaultInitialStep =
    params.cartIdEditing && isProductReviewFromCart
      ? ProductWizardStep.PickerReview
      : ProductWizardStep.ProductHome;

  const {
    cartIdEditing = '',
    id = '',
    pickerAspectKey = '',
    slotKey = '',
    step = defaultInitialStep,
    rewardBenefitId: rewardBenefitIdParam,
    rewardId: rewardIdParam,
    offerId: offerIdParam,
  } = params || {};

  const {
    flow,
    menuObject,
    selectedProduct,
    hasInAppNavigationRef,
    saveSlotsSelectionSnapshot,
    clearSlotsSelectionSnapshot,
    hasUnsavedChanges,
    setShowCloseDialog,
    setFlowDirection,
  } = useProductWizardContext();

  const urlFlowStep: FlowStep = useMemo(
    () => ({
      pickerAspectKey,
      slotKey,
      step: step as ProductWizardStep,
    }),
    [pickerAspectKey, slotKey, step]
  );

  const { currentFlowStep, currentStepIndex } = resolveCurrentFlowStep(flow, urlFlowStep);
  const nextFlowStep: FlowStep | undefined = flow[currentStepIndex + 1];

  const navigateToStep = useCallback(
    ({ pickerAspectKey = '', slotKey = '', step }: FlowStep) => {
      hasInAppNavigationRef.current = true;
      const updatedParams = {
        ...params,
        pickerAspectKey,
        slotKey,
        step,
      };
      router.setParams(updatedParams);
    },
    [hasInAppNavigationRef, params, router]
  );

  const resetToProductHome = useCallback(() => {
    navigateToStep({
      step: ProductWizardStep.ProductHome,
      slotKey: '',
      pickerAspectKey: '',
    });
  }, [navigateToStep]);

  const routeRedirect = useMemo(
    () =>
      getWizardRouteRedirectTarget({
        urlStep: urlFlowStep,
        flow,
        menuObject,
        selectedProduct,
        indexedMenu,
        hasInAppNavigation: hasInAppNavigationRef.current,
        cartIdEditing,
        isProductReviewFromCart,
        isAuthenticated,
        hasCartEntries,
      }),
    [
      urlFlowStep,
      flow,
      menuObject,
      selectedProduct,
      indexedMenu,
      hasInAppNavigationRef,
      cartIdEditing,
      isProductReviewFromCart,
      isAuthenticated,
      hasCartEntries,
    ]
  );

  const goToNextStep = useCallback(() => {
    setFlowDirection('forward');
    navigateToStep(nextFlowStep);
  }, [navigateToStep, nextFlowStep, setFlowDirection]);

  const goToPreviousStep = useCallback(() => {
    if (currentFlowStep.step === ProductWizardStep.ProductCustomization) {
      if (hasUnsavedChanges()) {
        setShowCloseDialog(true);
        return;
      }

      clearSlotsSelectionSnapshot();

      setFlowDirection('backward');
      navigateToStep({
        step: flow.length ? ProductWizardStep.PickerReview : ProductWizardStep.ProductHome,
      });
      return;
    }

    const isInitialStep = currentFlowStep.step === ProductWizardStep.ProductHome || cartIdEditing;
    const previousFlowStep: FlowStep | undefined = isInitialStep
      ? undefined
      : flow[currentStepIndex - 1];

    if (previousFlowStep) {
      setFlowDirection('backward');
      navigateToStep(previousFlowStep);
      return;
    }

    router.dismiss();
  }, [
    setFlowDirection,
    currentFlowStep.step,
    cartIdEditing,
    flow,
    currentStepIndex,
    router,
    hasUnsavedChanges,
    clearSlotsSelectionSnapshot,
    navigateToStep,
    setShowCloseDialog,
  ]);

  const goToCustomization = useCallback(
    (slotKeyToCustomize: string) => {
      setFlowDirection('forward');
      saveSlotsSelectionSnapshot();
      navigateToStep({
        step: ProductWizardStep.ProductCustomization,
        slotKey: slotKeyToCustomize,
      });
    },
    [navigateToStep, saveSlotsSelectionSnapshot, setFlowDirection]
  );

  // We pass the offerId in 2 different ways:
  // 1. as the id param eg: menu/offer/123
  // 2. as offerId param eg: menu/picker/123?offerId=123
  let offerId: string | undefined = undefined;
  if (offerIdParam && indexedOffers.find(offerIdParam)) {
    offerId = offerIdParam;
  } else if (id && indexedOffers.find(id)) {
    offerId = id;
  }

  let rewardId = rewardIdParam;
  if (!rewardIdParam && cartIdEditing && appliedRewards[cartIdEditing]) {
    rewardId = appliedRewards[cartIdEditing].sanityId;
  }
  let rewardBenefitId = rewardBenefitIdParam;
  if (!rewardBenefitIdParam && cartIdEditing && appliedRewards[cartIdEditing]) {
    rewardBenefitId = appliedRewards[cartIdEditing].rewardBenefitId;
  }

  return {
    cartIdEditing,
    isProductReviewFromCart,
    currentFlowStep: currentFlowStep as FlowStep,
    id,
    offerId,
    nextFlowStep,
    rewardBenefitId,
    rewardId,
    closeWizard: () => {
      if (router.canGoBack()) {
        router.back();
        return;
      }
      if (router.canDismiss()) {
        router.dismiss();
        return;
      }
      router.replace(routes.menu);
    },
    goToCustomization,
    goToNextStep,
    goToPreviousStep,
    routeRedirect,
    resetToProductHome,
  };
};
